import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';
import type { ApiClient, FetchLike } from '@dpem/contracts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { createWebApiClient } from '@/lib/api/client';
import { shouldRetryQuery } from '@/lib/api/errors';
import { useSessionStore } from '@/lib/auth/session.store';

/**
 * The api client and the query cache, provided rather than imported.
 *
 * Both are per-app-instance, not module singletons: two of them in one process
 * is exactly what the test suite needs (a fake fetch per test, an empty cache per
 * test), and a module-level client would make that impossible without resetting
 * global state between tests — the kind of shared mutable state that turns a
 * green suite into a flaky one.
 */

const ApiContext = createContext<ApiClient | null>(null);

export const useApi = (): ApiClient => {
  const client = useContext(ApiContext);
  if (client === null) {
    throw new Error('useApi must be used inside <ApiProvider>');
  }
  return client;
};

/**
 * Cache policy, chosen for method data rather than accepted from the defaults.
 *
 * A published method does not change while you are reading it, so a 30s stale
 * window removes a refetch on every panel expand. `refetchOnWindowFocus` is off:
 * this is a workspace people leave open beside a meeting, and a refetch storm on
 * every alt-tab is both wasteful and visually noisy. Authoring surfaces that need
 * fresher data set their own `staleTime` — the default is the floor, not a rule.
 */
export const createAppQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: shouldRetryQuery,
      },
      mutations: {
        // Never. A retried mutation is a second command, and every write in this
        // platform is a domain command with an audit entry and an expected
        // revision. Retrying is how you publish twice.
        retry: false,
      },
    },
  });

export interface ApiProviderProps {
  children: ReactNode;
  /** Injected by tests; production uses the global `fetch`. */
  fetch?: FetchLike;
  baseUrl?: string;
  /** Injected by tests that want to seed or assert on the cache. */
  queryClient?: QueryClient;
}

export function ApiProvider({ children, fetch, baseUrl, queryClient }: ApiProviderProps) {
  // `useRef` rather than `useMemo`: a client rebuilt on a dependency change would
  // be a new object identity in every consumer, and React Query would treat the
  // resulting refetches as legitimate. This is created once per mount, by design.
  const cacheRef = useRef<QueryClient | null>(queryClient ?? null);
  cacheRef.current ??= createAppQueryClient();
  const cache = cacheRef.current;

  const client = useMemo(
    () =>
      createWebApiClient({
        fetch,
        baseUrl,
        onUnauthenticated: () => {
          const session = useSessionStore.getState();
          // A 401 on the boot refresh means "no cookie", which is the normal
          // state of a first visit — not an expiry. Only a session that *was*
          // live gets the "your session ended" message, because only that user
          // was interrupted.
          if (session.status === 'authenticated') {
            session.expire();
            // Drop every cached response: they were fetched as someone who is no
            // longer signed in, and the next user of this tab must not see them.
            cache.clear();
          }
        },
      }),
    [fetch, baseUrl, cache],
  );

  return (
    <QueryClientProvider client={cache}>
      <ApiContext.Provider value={client}>{children}</ApiContext.Provider>
    </QueryClientProvider>
  );
}
